/**
 * Every string sign mode shows (FIL-8c).
 *
 * Sentence case, no emoji. The refusals name what is wrong and what the person
 * can do instead — a filing that will not open here still downloads.
 */
export const SIGNING_COPY = {
  tools: {
    /**
     * Each is a toggle in the toolbar, on `zoom.toFitWidth`'s precedent: the
     * label names what pressing it will do, not the state it is already in.
     */
    place: (kind: string) => `Place a ${kind.toLowerCase()}`,
    cancel: (kind: string) => `Stop placing a ${kind.toLowerCase()}`,
    /**
     * The pencil is its own toggle, not a "place": it is not dropped on a click
     * like the other two but drawn, and it stays armed across strokes — so its
     * label names the act rather than a placement.
     */
    draw: 'Draw on the document',
    stopDraw: 'Stop drawing',
  },

  field: {
    signature: 'Signature',
    text: 'Text field',
    drawing: 'Drawing',
    /** Names the field for a screen reader; several boxes otherwise read alike. */
    label: (kind: string, page: number) => `${kind} on page ${String(page)}`,
    remove: (kind: string) => `Remove ${kind.toLowerCase()}`,
    resize: 'Resize',
    textPlaceholder: 'Type here',
  },

  /** The freehand drawing surface, shown over the page while the pencil is armed. */
  draw: {
    /** The canvas's accessible name — it is an `img` role like `SignaturePad`. */
    surface: (page: number) => `Drawing surface on page ${String(page)}`,
  },

  undo: {
    /** Removes the last local edit — placement, move, typing, drawing, delete. */
    action: 'Undo last edit',
  },

  save: {
    action: 'Save signed copy',
    working: 'Saving',
    done: (fileName: string) => `Saved as ${fileName}`,
  },

  discard: {
    /** Closing with fields placed and nothing saved. */
    prompt: 'Close without saving? The fields you placed will be lost.',
    confirm: 'Close without saving',
    cancel: 'Keep placing',

    /**
     * The mobile bottom bar's way out of sign mode.
     *
     * On a phone the bar swaps to Cancel and Save the moment a tool is armed or
     * a field lands, because there is no room to carry paging, both tools and a
     * save at a thumb-sized target. Cancel is the leave; on desktop that job is
     * still done by pressing the armed tool again, which the bar has room to
     * keep showing.
     *
     * `action` is the visible word and `label` is the accessible name — which
     * **starts with that same word**, because WCAG 2.5.3 asks that a control's
     * name contain its visible text, and because "Cancel" alone does not say
     * what it costs.
     */
    action: 'Cancel',
    label: 'Cancel signing and discard placed fields',
    /**
     * Cancelling with something already placed. Distinct from `prompt` above,
     * which is about *closing the reader*: the same fields are lost, but a
     * reader who meant to close and one who meant to back out of sign mode are
     * answering different questions.
     */
    cancelPrompt: 'Discard the fields you placed?',
  },

  needsSignature: {
    prompt: 'Set up your signature before signing a document.',
    action: 'Set up your signature',
  },

  errors: {
    encrypted:
      'This document is password-protected, so it cannot be signed here. Download it to sign it in a PDF reader.',
    'already-signed':
      'This document already carries a digital signature. Signing it here would break that signature, so it is left alone.',
    'no-fields': 'Place your signature on the document before saving.',
    failed: 'That signed copy could not be created.',
    /** A refusal from the upload itself — the API's own words win where there are any. */
    upload: 'The signed copy could not be uploaded.',
  },
} as const;
